// ============================================================================
// CadernoCockpitRepository.js — Encapsula todas as chamadas Firestore
// do componente CadernoCockpit (Fase 6.2 — extração de repositories).
// ============================================================================

import { db } from '../../shared/api/firebase/config';
import { collection, query, where, onSnapshot, limit, doc, setDoc, addDoc, orderBy, serverTimestamp, getDocs, getDoc, deleteDoc, runTransaction } from 'firebase/firestore';
import { FIREBASE_CONFIG } from '../../config/Config';
import { FS } from '../../shared/constants/firestorePaths';
import { SearchEngine } from '../../utils/SearchEngine';

const appId = FIREBASE_CONFIG.projectId;

export function getWorkspaceDocRef(workspaceId) {
  if (!workspaceId || !appId) return null;
  return doc(db, 'artifacts', appId, 'public', 'data', 'autos_workspaces', workspaceId);
}

export function getDocumentsCollectionRef() {
  return collection(db, 'artifacts', appId, 'public', 'data', 'documents');
}

// ─── 1. Alias / Renomear caderno ────────────────────────────────────────
export async function updateWorkspaceAlias(workspaceId, alias) {
  if (!workspaceId || !appId) return;
  const ref = doc(db, 'artifacts', appId, 'public', 'data', 'autos_workspaces', workspaceId);
  await setDoc(ref, { alias, updatedAt: serverTimestamp() }, { merge: true });
}

// ─── 2. Buscar documentos publicados vinculados ao caderno ────────────
export async function fetchPublishedDocs(workspaceId) {
  if (!workspaceId || !appId) return [];
  const docsRef = collection(db, 'artifacts', appId, 'public', 'data', 'documents');
  const q = query(docsRef, where('cadernoId', '==', workspaceId), limit(100));
  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
  rows.sort((a, b) => {
    const av = a?.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
    const bv = b?.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
    return bv - av;
  });
  return rows;
}

export function subscribePublishedDocs(workspaceId, callback) {
  if (!workspaceId || !appId) {
    callback([]);
    return () => {};
  }
  const docsRef = collection(db, 'artifacts', appId, 'public', 'data', 'documents');
  const q = query(docsRef, where('cadernoId', '==', workspaceId), limit(100));
  const unsub = onSnapshot(
    q,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      rows.sort((a, b) => {
        const av = a?.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const bv = b?.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return bv - av;
      });
      callback(rows);
    },
    () => callback([])
  );
  return unsub;
}

// ─── 3. Persistir peças ─────────────────────────────────────────────────
export async function persistPieces(workspaceId, pieces) {
  const ref = getWorkspaceDocRef(workspaceId);
  if (!ref) return;
  await setDoc(ref, { pieces, updatedAt: serverTimestamp() }, { merge: true });
}

// ─── 4. Salvar Informações Gerais ───────────────────────────────────────
export async function saveInfoGerais(workspaceId, { numeroCNJ, status, juizo, comarca, metadata }) {
  const ref = getWorkspaceDocRef(workspaceId);
  if (!ref) return;
  const payload = { juizo, comarca, metadata, updatedAt: serverTimestamp() };
  if (numeroCNJ !== undefined) payload.numeroCNJ = numeroCNJ;
  if (status !== undefined) payload.status = status;
  await setDoc(ref, payload, { merge: true });
}

// ─── 5. Salvar Status do Portal ─────────────────────────────────────────
export async function savePortalStatus(workspaceId, payload) {
  const ref = getWorkspaceDocRef(workspaceId);
  if (!ref) return;
  await setDoc(ref, { ...payload, updatedAt: serverTimestamp() }, { merge: true });
}

// ─── 6. Salvar Partes ───────────────────────────────────────────────────
export async function savePartes(workspaceId, partesPayload) {
  const ref = getWorkspaceDocRef(workspaceId);
  if (!ref) return;
  await setDoc(ref, { partes: partesPayload, updatedAt: serverTimestamp() }, { merge: true });
}

// ─── 7. Salvar Co-Partes ────────────────────────────────────────────────
export async function saveCoPartes(workspaceId, partiesRefs, partiesRefIds) {
  const ref = getWorkspaceDocRef(workspaceId);
  if (!ref) return;
  await setDoc(ref, { partiesRefs, partiesRefIds, updatedAt: serverTimestamp() }, { merge: true });
}

// ─── 8. Salvar Equipe do Caso ───────────────────────────────────────────
export async function saveCaseTeam(workspaceId, caseTeam, caseTeamIds) {
  const ref = getWorkspaceDocRef(workspaceId);
  if (!ref) return;
  await setDoc(ref, { caseTeam, caseTeamIds, updatedAt: serverTimestamp() }, { merge: true });
}

// ─── 9. Salvar Sigilo / Prioridade ──────────────────────────────────────
export async function saveSigilo(workspaceId, nivelSigilo, prioridade) {
  const ref = getWorkspaceDocRef(workspaceId);
  if (!ref) return;
  await setDoc(ref, { nivelSigilo, prioridade, updatedAt: serverTimestamp() }, { merge: true });
}

// ─── 10. Migrar Drive para pasta de processo ─────────────────────────────
export async function migrateDriveToMatterFolder(workspaceId, payload) {
  const ref = getWorkspaceDocRef(workspaceId);
  if (!ref) return;
  await setDoc(ref, { ...payload, updatedAt: serverTimestamp() }, { merge: true });
}

// ─── 11. Salvar Número CNJ (fusão Processos/Caderno — MC01) ─────────────
export async function saveNumeroCNJ(workspaceId, numeroCNJ) {
  const ref = getWorkspaceDocRef(workspaceId);
  if (!ref) return;
  await setDoc(ref, { numeroCNJ, updatedAt: serverTimestamp() }, { merge: true });
}

// ─── Fase 5 (PRE_VOO_LIVRO_PROCESSUAL_GERADO_20260901.md) — link do livro ──
// cadernoMdWebViewLink: link do Drive pro CADERNO_<...>.md gerado (Fase 2) —
// gravado uma vez (o arquivo é editado no lugar por regenerações futuras,
// Fase 3, o ID do Drive não muda), lido pelo botão "Abrir Caderno de
// Trabalho ↗" na aba Resumo.
export async function saveCadernoMdLink(workspaceId, cadernoMdWebViewLink) {
  const ref = getWorkspaceDocRef(workspaceId);
  if (!ref) return;
  await setDoc(ref, { cadernoMdWebViewLink, updatedAt: serverTimestamp() }, { merge: true });
}

// ─── 12. Salvar relações entre processos (fusão — MC02) ──────────────────
// processoPrincipalId: ID do workspace do qual este é apenso/incidente
//   (relação FORMAL, mesma lógica do "Processo Originário" do Projudi).
// apensos: IDs de workspace que são apensos DESTE processo (mão inversa).
// processosCorrelatos: IDs de workspace relacionados por fato/estratégia,
//   sem relação de apenso formal.
// MC50 (Fase F2b, 22/08/2026): estes 3 campos guardam o ID DO DOCUMENTO do
// workspace, não o CNJ como a versão original (MC02) previa — confirmado
// contra o Firestore real que nenhum registro usa o schema antigo ainda,
// sem migração necessária. Motivo da mudança: a maioria dos cadernos reais
// é pré-processual, sem CNJ — vincular só por CNJ (como o acordeão
// "Vínculos" do modal Metadados faz hoje) tornava a maioria dos casos reais
// impossível de linkar. ID de workspace funciona sempre.
export async function saveProcessRelations(workspaceId, { processoPrincipalId = null, apensos = [], processosCorrelatos = [] } = {}) {
  const ref = getWorkspaceDocRef(workspaceId);
  if (!ref) return;
  await setDoc(ref, { processoPrincipalId, apensos, processosCorrelatos, updatedAt: serverTimestamp() }, { merge: true });
}

// ─── 12b. Vínculos bidirecionais entre processos (Fase F2b — MC50) ───────
// Substitui o handleSalvarVinculo antigo (CadernoCockpit.jsx), que só
// gravava num lado da relação — vincular A como apenso de B nunca
// atualizava o apensos[] de B. Aqui os dois lados são gravados na mesma
// transação, atômicos (mesmo padrão de updateMovementResumo).
export async function linkProcessRelation(workspaceId, relationType, targetWorkspaceId) {
  if (!workspaceId || !targetWorkspaceId || workspaceId === targetWorkspaceId) return;
  const ref = getWorkspaceDocRef(workspaceId);
  const targetRef = getWorkspaceDocRef(targetWorkspaceId);
  if (!ref || !targetRef) return;

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const targetSnap = await tx.get(targetRef);
    if (!snap.exists() || !targetSnap.exists()) return;
    const data = snap.data();
    const targetData = targetSnap.data();

    if (relationType === 'principal') {
      // este vira apenso do alvo (o alvo passa a ser o principal)
      tx.update(ref, { processoPrincipalId: targetWorkspaceId, updatedAt: serverTimestamp() });
      const targetApensos = Array.isArray(targetData.apensos) ? targetData.apensos : [];
      if (!targetApensos.includes(workspaceId)) {
        tx.update(targetRef, { apensos: [...targetApensos, workspaceId], updatedAt: serverTimestamp() });
      }
    } else if (relationType === 'apenso') {
      // o alvo vira apenso deste
      const apensos = Array.isArray(data.apensos) ? data.apensos : [];
      if (!apensos.includes(targetWorkspaceId)) {
        tx.update(ref, { apensos: [...apensos, targetWorkspaceId], updatedAt: serverTimestamp() });
      }
      tx.update(targetRef, { processoPrincipalId: workspaceId, updatedAt: serverTimestamp() });
    } else if (relationType === 'correlato') {
      // simétrico: os dois lados ganham a referência um do outro
      const correlatos = Array.isArray(data.processosCorrelatos) ? data.processosCorrelatos : [];
      const targetCorrelatos = Array.isArray(targetData.processosCorrelatos) ? targetData.processosCorrelatos : [];
      if (!correlatos.includes(targetWorkspaceId)) {
        tx.update(ref, { processosCorrelatos: [...correlatos, targetWorkspaceId], updatedAt: serverTimestamp() });
      }
      if (!targetCorrelatos.includes(workspaceId)) {
        tx.update(targetRef, { processosCorrelatos: [...targetCorrelatos, workspaceId], updatedAt: serverTimestamp() });
      }
    }
  });
}

export async function unlinkProcessRelation(workspaceId, relationType, targetWorkspaceId) {
  if (!workspaceId || !targetWorkspaceId) return;
  const ref = getWorkspaceDocRef(workspaceId);
  const targetRef = getWorkspaceDocRef(targetWorkspaceId);
  if (!ref || !targetRef) return;

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const targetSnap = await tx.get(targetRef);
    if (!snap.exists()) return;
    const data = snap.data();
    const targetData = targetSnap.exists() ? targetSnap.data() : {};

    if (relationType === 'principal') {
      tx.update(ref, { processoPrincipalId: null, updatedAt: serverTimestamp() });
      if (targetSnap.exists()) {
        const targetApensos = (Array.isArray(targetData.apensos) ? targetData.apensos : []).filter((id) => id !== workspaceId);
        tx.update(targetRef, { apensos: targetApensos, updatedAt: serverTimestamp() });
      }
    } else if (relationType === 'apenso') {
      const apensos = (Array.isArray(data.apensos) ? data.apensos : []).filter((id) => id !== targetWorkspaceId);
      tx.update(ref, { apensos, updatedAt: serverTimestamp() });
      if (targetSnap.exists()) {
        tx.update(targetRef, { processoPrincipalId: null, updatedAt: serverTimestamp() });
      }
    } else if (relationType === 'correlato') {
      const correlatos = (Array.isArray(data.processosCorrelatos) ? data.processosCorrelatos : []).filter((id) => id !== targetWorkspaceId);
      tx.update(ref, { processosCorrelatos: correlatos, updatedAt: serverTimestamp() });
      if (targetSnap.exists()) {
        const targetCorrelatos = (Array.isArray(targetData.processosCorrelatos) ? targetData.processosCorrelatos : []).filter((id) => id !== workspaceId);
        tx.update(targetRef, { processosCorrelatos: targetCorrelatos, updatedAt: serverTimestamp() });
      }
    }
  });
}

// ─── 13. Salvar Status do Processo (fusão — MC03) ─────────────────────────
// Mesmos valores do Kanban antigo em ProcessManager.jsx: Ativo/Suspenso/
// Recurso/Liminar/Arquivado. Não existia em autos_workspaces até este commit.
export async function saveStatus(workspaceId, status) {
  const ref = getWorkspaceDocRef(workspaceId);
  if (!ref) return;
  await setDoc(ref, { status, updatedAt: serverTimestamp() }, { merge: true });
}

// ─── 14. Movimentações (fusão — MC06) ─────────────────────────────────────
// Porte de addMovement/subscribeMovements de ProcessManagerRepository.js
// (branch feature/movimentacoes-processuais, MC02) — mesmo schema de
// documento, só troca o pai (autos_workspaces em vez de processes).
export async function addMovement(workspaceId, payload) {
  const movementsRef = collection(db, ...FS.workspaceMovements(workspaceId, appId));
  const createdRef = await addDoc(movementsRef, { ...payload, workspaceId, createdAt: serverTimestamp() });
  return createdRef.id;
}

export function subscribeMovements(workspaceId, callback) {
  const q = query(
    collection(db, ...FS.workspaceMovements(workspaceId, appId)),
    orderBy('dataMovimento', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const movements = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(movements);
  });
}

// ─── 15. Índice de busca (fusão — MC07) ───────────────────────────────────
// Pré-requisito da Fase G: Conflito de Interesse, SmartEntityLinker,
// ClientRepository e AdminRepository dependem de busca por searchKeywords,
// que autos_workspaces não tinha até este commit. Reaproveita o tokenizer
// já existente (SearchEngine.generateKeywords), não reinventa. Cobre
// alias + clientName + numeroCNJ nesta fase — parteContraria (necessário
// para o scanner de conflito, MC25) é decidido na hora de mapear o shape
// de `partes`, não aqui.
// ─── 16. Resumo de movimentação, versionado (fusão — MC12) ────────────────
// Guardrail (relatório de arquitetura, Seção 4.4, item 3): nunca sobrescreve
// silenciosamente — se já existe um resumo, empurra para resumoHistorico[]
// antes de gravar o novo. Transação atômica: não pode haver janela em que o
// resumo "some" entre ler o estado atual e escrever o novo.
export async function updateMovementResumo(workspaceId, movementId, resumoPayload) {
  const movementRef = doc(db, ...FS.workspaceMovements(workspaceId, appId), movementId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(movementRef);
    const current = snap.exists() ? snap.data() : {};
    const resumoHistorico = Array.isArray(current?.resumoHistorico) ? current.resumoHistorico : [];
    const nextHistorico = current?.resumo ? [...resumoHistorico, current.resumo] : resumoHistorico;
    tx.update(movementRef, {
      resumo: { ...resumoPayload, status: resumoPayload.status || 'NAO_REVISADO' },
      resumoHistorico: nextHistorico,
    });
  });
}

export async function markMovementResumoReviewed(workspaceId, movementId, revisadoPor, corrigido = false) {
  const movementRef = doc(db, ...FS.workspaceMovements(workspaceId, appId), movementId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(movementRef);
    if (!snap.exists()) return;
    const current = snap.data();
    tx.update(movementRef, {
      resumo: {
        ...(current.resumo || {}),
        status: corrigido ? 'REVISADO_CORRIGIDO' : 'REVISADO_OK',
        revisadoPor,
        revisadoEm: new Date().toISOString(),
      },
    });
  });
}

// ─── 17. Atualização de campos de uma movimentação já existente ──────────
// (Constituição Processos Art. 13.7 — 22/08/2026). Antes só dava pra
// anexar arquivo/definir usuário no momento de CRIAR a movimentação
// (MovementCaptureForm). Esta função cobre o caso real: movimentação já
// existe (veio do DataJud, campos genéricos por natureza da API pública),
// e o titular fornece um PDF/print mais completo depois — não sobrescreve
// campos não informados (merge:true), e nunca sobe arquivo pro
// Storage/Firestore: `anexo` aqui é referência ao Google Drive (mesmo
// padrão do Motor de Anexos em useFileHub.js — driveFileId + webViewLink),
// nunca upload.
export async function updateMovement(workspaceId, movementId, updates) {
  const movementRef = doc(db, ...FS.workspaceMovements(workspaceId, appId), movementId);
  await setDoc(movementRef, updates, { merge: true });
}

export async function saveSearchKeywords(workspaceId, { alias = '', clientName = '', numeroCNJ = '' } = {}) {
  const ref = getWorkspaceDocRef(workspaceId);
  if (!ref) return;
  const searchKeywords = [
    ...SearchEngine.generateKeywords(alias),
    ...SearchEngine.generateKeywords(clientName),
    String(numeroCNJ || '').replace(/\D/g, ''),
  ].filter(Boolean);
  await setDoc(ref, { searchKeywords: [...new Set(searchKeywords)], updatedAt: serverTimestamp() }, { merge: true });
}

// ─── 17. Prazos / Audiências (fusão — MC20) ───────────────────────────────
// Sai do placeholder "(Fase 2 — DataJud)" do acordeão `prazos` em
// CadernoCockpit.jsx. Entrada manual, mesma filosofia já decidida para
// movimentações: manual primeiro, automação (DataJud) depois, se algum dia
// justificar o esforço.
export async function savePrazosAudiencias(workspaceId, { prazos = [], audiencias = [] } = {}) {
  const ref = getWorkspaceDocRef(workspaceId);
  if (!ref) return;
  await setDoc(ref, { prazos, audiencias, updatedAt: serverTimestamp() }, { merge: true });
}

// ─── 18. Notas do Caderno — camada `caderno_notas` (Constituição Processos ART. 20) ──
// Subcoleção IRMÃ de `movements` (§14) para o "histórico processual completo":
// notas de trabalho (estratégia/pesquisa/pendência/observação) + transcrição
// comentada de atos oficiais, intercaladas na linha do tempo pela `data`.
// Espelha o padrão de addMovement/subscribeMovements — CRUD próprio, nunca
// escreve em `movements` nem em campos do workspace (isolamento, Art. 14).
export const CADERNO_NOTA_CAMADAS = ['AUTOS_OFICIAIS', 'NOTAS_TRABALHO'];
export const CADERNO_NOTA_TIPOS = [
  'DESPACHO_TRANSCRITO',
  'DECISAO_TRANSCRITA',
  'PESQUISA_JURIS',
  'ESTRATEGIA',
  'PENDENCIA',
  'OBSERVACAO',
  // SINTESE_PECA (ART. 20.2, Nível 1): esqueleto de uma peça protocolada —
  // pedidos/causa de pedir/fundamentos/valor da causa. SEMPRE
  // camada: NOTAS_TRABALHO (é a análise, não a transcrição literal), com
  // pecaRef. Não substitui o .docx da peça (Motor de Anexos).
  'SINTESE_PECA',
];
// Campos que uma nota `camada: AUTOS_OFICIAIS` NÃO pode alterar depois de
// salva (mesma disciplina de `descricaoOficial` — ART. 20.3): correção =
// nota nova, nunca edição.
const CADERNO_NOTA_CAMPOS_IMUTAVEIS_AUTOS_OFICIAIS = ['texto', 'titulo', 'data', 'camada'];

export async function addNota(workspaceId, payload = {}) {
  if (!workspaceId || !appId) return null;
  const camada = payload.camada;
  const tipo = payload.tipo;
  if (!CADERNO_NOTA_CAMADAS.includes(camada)) {
    throw new Error(`caderno_notas: camada inválida "${camada}" (esperado: ${CADERNO_NOTA_CAMADAS.join(' | ')})`);
  }
  if (!CADERNO_NOTA_TIPOS.includes(tipo)) {
    throw new Error(`caderno_notas: tipo inválido "${tipo}" (esperado: ${CADERNO_NOTA_TIPOS.join(' | ')})`);
  }
  // SINTESE_PECA é sempre NOTAS_TRABALHO (ART. 20.2) e pede pecaRef.
  const camadaFinal = tipo === 'SINTESE_PECA' ? 'NOTAS_TRABALHO' : camada;
  if (tipo === 'SINTESE_PECA' && !payload.pecaRef) {
    console.warn('caderno_notas: SINTESE_PECA salva sem pecaRef — recomenda-se vincular à peça.');
  }
  const notasRef = collection(db, ...FS.workspaceCadernoNotas(workspaceId, appId));
  const nota = {
    data: String(payload.data || '').slice(0, 10),
    autor: payload.autor || null,
    camada: camadaFinal,
    tipo,
    titulo: String(payload.titulo || '').trim(),
    texto: String(payload.texto || '').trim(),
    movimentoRef: payload.movimentoRef || null,
    pecaRef: payload.pecaRef || null,
    links: Array.isArray(payload.links)
      ? payload.links
          .map((l) => ({ label: String(l?.label || '').trim(), url: String(l?.url || '').trim() }))
          .filter((l) => l.label || l.url)
      : [],
    status: tipo === 'PENDENCIA' ? (payload.status === 'RESOLVIDA' ? 'RESOLVIDA' : 'ABERTA') : null,
    origem: 'MANUAL',
    workspaceId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const createdRef = await addDoc(notasRef, nota);
  return createdRef.id;
}

export function subscribeNotas(workspaceId, callback) {
  if (!workspaceId || !appId) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(db, ...FS.workspaceCadernoNotas(workspaceId, appId)),
    orderBy('data', 'desc')
  );
  return onSnapshot(
    q,
    (snapshot) => callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
    () => callback([])
  );
}

// updateNota — merge. Numa nota `camada: AUTOS_OFICIAIS`, recusa alteração
// dos campos imutáveis (ART. 20.3) — só `status`, `links`, `movimentoRef`,
// `pecaRef` podem mudar depois de salva. Nota `NOTAS_TRABALHO` é livre.
export async function updateNota(workspaceId, notaId, updates = {}) {
  if (!workspaceId || !notaId || !appId) return;
  const notaRef = doc(db, ...FS.workspaceCadernoNotas(workspaceId, appId), notaId);
  const snap = await getDoc(notaRef);
  if (!snap.exists()) throw new Error(`caderno_notas: nota ${notaId} não existe`);
  const current = snap.data() || {};

  if (updates.tipo !== undefined && !CADERNO_NOTA_TIPOS.includes(updates.tipo)) {
    throw new Error(`caderno_notas: tipo inválido "${updates.tipo}"`);
  }

  if (current.camada === 'AUTOS_OFICIAIS') {
    const bloqueado = CADERNO_NOTA_CAMPOS_IMUTAVEIS_AUTOS_OFICIAIS.filter(
      (campo) => updates[campo] !== undefined && updates[campo] !== current[campo]
    );
    if (bloqueado.length > 0) {
      throw new Error(
        `caderno_notas: nota AUTOS_OFICIAIS é imutável — não é possível alterar ${bloqueado.join(', ')}. ` +
        'Correção = nota nova (Constituição Processos ART. 20.3).'
      );
    }
  }

  const patch = { ...updates, updatedAt: serverTimestamp() };
  if (Array.isArray(patch.links)) {
    patch.links = patch.links
      .map((l) => ({ label: String(l?.label || '').trim(), url: String(l?.url || '').trim() }))
      .filter((l) => l.label || l.url);
  }
  const tipoFinal = patch.tipo || current.tipo;
  if (tipoFinal === 'PENDENCIA') {
    if (patch.status !== undefined) patch.status = patch.status === 'RESOLVIDA' ? 'RESOLVIDA' : 'ABERTA';
  } else if (patch.tipo !== undefined) {
    patch.status = null;
  }
  // SINTESE_PECA é sempre NOTAS_TRABALHO (ART. 20.2).
  if (tipoFinal === 'SINTESE_PECA') {
    patch.camada = 'NOTAS_TRABALHO';
    const pecaRefFinal = patch.pecaRef !== undefined ? patch.pecaRef : current.pecaRef;
    if (!pecaRefFinal) {
      console.warn('caderno_notas: SINTESE_PECA sem pecaRef — recomenda-se vincular à peça.');
    }
  }
  await setDoc(notaRef, patch, { merge: true });
}

export async function deleteNota(workspaceId, notaId) {
  if (!workspaceId || !notaId || !appId) return;
  await deleteDoc(doc(db, ...FS.workspaceCadernoNotas(workspaceId, appId), notaId));
}
